/**
 * Onboarding overlay registry — every id, priority and string in one place.
 *
 * Bump VERSION when an overlay's copy changes enough that previously-dismissed
 * users should see it again. Keys are derived, never hand-written.
 */

export const VERSION = 'v1';

export type OverlayLane = 'studio' | 'campaign' | 'calendar';

/** Higher wins. Only one overlay may occupy a screen at a time. */
export const PRIORITY = {
  blocker: 40,
  welcome: 30,
  coach: 20,
  capability: 10,
} as const;

export type OverlayKind = keyof typeof PRIORITY;

/** localStorage key for a per-user overlay. */
export const userKey = (id: string) => `admeru_overlay__${id}__${VERSION}`;

/** localStorage key for per-organization state (setup progress, snooze). */
export const orgKey = (id: string, orgId: string) =>
  `admeru_overlay__${id}__${orgId}__${VERSION}`;

/* ------------------------------------------------------------------ */
/* Welcome path                                                        */
/* ------------------------------------------------------------------ */

export const WELCOME_ID = 'welcome_path';

export interface LaneOption {
  lane: OverlayLane;
  title: string;
  body: string;
  meta: string;
  route: string;
}

export const WELCOME_COPY = {
  title: 'What do you want to get out the door first?',
  subtitle:
    "Pick one. You can do the rest later — this just sets your first three minutes.",
  skip: "Skip — I'll look around",
  footnote: 'Shown once. Replay from Help.',
  lanes: [
    {
      lane: 'studio',
      title: 'Design an ad creative',
      body: 'Product details in, image or video ad out. No design tool needed.',
      meta: '~2 MIN · AD STUDIO',
      route: '/ad-studio',
    },
    {
      lane: 'campaign',
      title: 'Launch a campaign',
      body:
        '5 ad variations, audience picked for you, published to Meta, Google, Pinterest and X.',
      meta: '~4 MIN · CAMPAIGNS',
      route: '/campaigns',
    },
    {
      lane: 'calendar',
      title: 'Plan the month',
      body: 'Schedule ads ahead and let them go live without you.',
      meta: '~2 MIN · CALENDAR',
      route: '/calendar',
    },
  ] as LaneOption[],
};

/* ------------------------------------------------------------------ */
/* Setup dock                                                          */
/* ------------------------------------------------------------------ */

export const SETUP_ID = 'setup_dock';
export const SETUP_SNOOZE_DAYS = 7;

export type SetupStepId =
  | 'brand_kit'
  | 'first_creative'
  | 'connect_account'
  | 'first_publish';

export interface SetupStepCopy {
  id: SetupStepId;
  title: string;
  body: string;
  cta: string;
  route: string;
}

export const SETUP_STEPS: SetupStepCopy[] = [
  {
    id: 'brand_kit',
    title: 'Add your brand',
    body:
      'Logo and colours once — every creative after this comes out on-brand.',
    cta: 'Open your brand',
    route: '/business?topic=voice',
  },
  {
    id: 'first_creative',
    title: 'Make your first creative',
    body: 'One sentence about the product is enough to start.',
    cta: 'Open Ad Studio',
    route: '/ad-studio',
  },
  {
    id: 'connect_account',
    title: 'Connect an ad account',
    body:
      'Needed once per organization before anything can publish. Takes about a minute.',
    cta: 'Connect Meta',
    route: '/settings?tab=connections',
  },
  {
    id: 'first_publish',
    title: 'Publish your first campaign',
    body: 'One button posts to every platform you connected.',
    cta: 'Create campaign',
    route: '/campaigns',
  },
];

/** Order the checklist by the lane the user chose in the welcome modal. */
export const laneOrder: Record<OverlayLane, SetupStepId[]> = {
  studio: ['brand_kit', 'first_creative', 'connect_account', 'first_publish'],
  campaign: ['connect_account', 'brand_kit', 'first_creative', 'first_publish'],
  calendar: ['connect_account', 'first_creative', 'brand_kit', 'first_publish'],
};

/* ------------------------------------------------------------------ */
/* Coach sequences                                                     */
/* ------------------------------------------------------------------ */

export interface CoachStepCopy {
  id: string;
  title: string;
  body: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  /** CSS selector for the element this bubble points at, resolved live at runtime. */
  anchor?: string;
}

export const COACH: Record<string, CoachStepCopy[]> = {
  studio_first_run: [
    {
      id: 'studio_brief',
      title: 'One sentence is enough',
      body:
        "Product, occasion, feeling. The AI writes the headline, picks the layout and lays out the type — you're not designing anything by hand.",
      placement: 'right',
    },
    {
      id: 'studio_look',
      title: 'Let the AI decide, or match a reference',
      body:
        'Drop in an image you like and the composition follows it. Otherwise the AI picks a layout that fits your product.',
      placement: 'left',
    },
    {
      id: 'studio_video',
      title: 'The same brief can come out as video',
      body:
        'Switch the format and you get an animated ad instead of a still — same details, no extra work.',
      placement: 'top',
    },
  ],
  campaign_variations: [
    {
      id: 'campaign_variations',
      title: 'Five versions go live together',
      body:
        'We keep spending on whichever one converts and quietly drop the rest.',
      placement: 'top',
    },
  ],
  campaign_audience: [
    {
      id: 'campaign_audience',
      title: 'Audience picked from your product',
      body:
        'Interests, age bands and geography were chosen for you. Open it to override anything.',
      placement: 'right',
    },
  ],
  // Ad Studio tour (mounted for /ad-studio via COACH_ROUTES). Step ids are versioned so users
  // who dismissed the old one-step tour still see what changed.
  studio_look: [
    {
      id: 'studio_brief_v2',
      title: 'One sentence is enough',
      body:
        "Say what you're selling and why now — product, offer or occasion, feeling. The AI writes the copy and designs the ad.",
      placement: 'right',
      anchor: '[data-coach="studio-brief"]',
    },
    {
      id: 'studio_products_v2',
      title: 'Your product, your photos',
      body:
        "Add up to 5 photos. 'Use photo as-is' builds the ad around your photo; 'Generate variations' puts your real product in new AI scenes. Several different products, like flavours? Choose 'Different products' and they appear together in one image.",
      placement: 'right',
      anchor: '[data-coach="studio-products"]',
    },
    {
      id: 'studio_look_v2',
      title: 'Pick the look — or let the AI',
      body:
        "Leave it on 'AI decides', tap a style, or upload an ad you like. We borrow its layout only — never its words or its product. A screenshot of the whole ad works best.",
      placement: 'left',
      anchor: '[data-coach="studio-look"]',
    },
  ],
  // Shown once, the moment a second product photo is added (CodeTemplateStudio mounts it).
  studio_product_range: [
    {
      id: 'studio_product_range',
      title: 'Different products?',
      body:
        "If these are different products — flavours, colours, a range — pick 'Different products'. We'll show them all together in one image and check every label.",
      placement: 'bottom',
      anchor: '[data-coach="studio-product-mode"]',
    },
  ],
  org_switcher: [
    {
      id: 'org_switcher',
      title: 'Run several brands from one login',
      body:
        'Switch here and everything follows — campaigns, creatives, media, budgets and connected accounts. Nothing is shared between organizations.',
      placement: 'bottom',
      anchor: '[data-coach="org-switcher"]',
    },
  ],
  calendar_failed: [
    {
      id: 'calendar_failed',
      title: "Here's why it failed",
      body:
        'Open the entry for the platform error and retry — nothing was charged.',
      placement: 'right',
    },
  ],
};

/**
 * Which coach sequence, if any, runs when the user lands on a route. First match
 * wins.
 *
 * The Ad Studio coach anchors to the Look & Composition panel, which only exists
 * on the code-engine Create tab. That self-sequences it after the first-visit
 * OverviewPanel: the bubble can't measure — so can't show — until the user is
 * past the welcome modal and actually in the Create flow. On the generic engine
 * (no such panel) it simply never resolves an anchor and stays dormant.
 *
 * The campaign coaches anchor to elements that only exist mid-conversation, so
 * they're claimed from inside those flows, not by route.
 */
export const COACH_ROUTES: { prefix: string; coachId: string }[] = [
  { prefix: '/ad-studio', coachId: 'studio_look' },
  { prefix: '/results', coachId: 'org_switcher' },
];

export const coachForRoute = (path: string): string | null =>
  COACH_ROUTES.find((r) => path.startsWith(r.prefix))?.coachId ?? null;

/* ------------------------------------------------------------------ */
/* Capability cards (empty states)                                     */
/* ------------------------------------------------------------------ */

export const CAPABILITY = {
  dashboard_roi: {
    title: 'Your return, tracked per platform',
    body:
      'Once a campaign is live, spend and revenue land here daily — Meta, Google, Pinterest and X side by side, no exports.',
    primary: { label: 'Launch first campaign', route: '/campaigns' },
    secondary: { label: 'How ROAS is measured' },
  },
  campaigns_empty: {
    title: 'One brief. Four platforms. No ad manager.',
    body:
      "Describe the product and the budget. Admeru writes 5 ad variations, picks the audience, then posts to Meta, Google, Pinterest and X. You never open a single one of them.",
    primary: { label: 'Create New Campaign', route: '/campaigns/new' },
    secondary: { label: 'Watch a 60-second run-through' },
  },
  calendar_empty: {
    title: 'Nothing scheduled yet',
    body:
      "Plan a month of ads in one sitting — pick the date, we publish at that hour whether you're online or not.",
    primary: { label: 'Schedule an ad', route: '/campaigns/new' },
    footnote: 'Or drag any draft campaign onto a date.',
  },
  media_empty: {
    title: 'Every image and video, in one place',
    body:
      'Product photos you upload and anything the AI generates land here, reusable across campaigns and downloadable any time.',
    primary: { label: 'Upload photos' },
    secondary: { label: 'Generate images' },
    footnote:
      'This library belongs to this organization. Media never crosses between organizations.',
  },
  creatives_empty: {
    title: 'Your creatives collect here',
    body:
      'Every generated image and animation is saved, re-editable and ready to drop into a campaign.',
    primary: { label: 'Create a creative', route: '/ad-studio' },
  },
} as const;

/* ------------------------------------------------------------------ */
/* Info badges (Type E) — feed straight into the existing HintBadge     */
/* ------------------------------------------------------------------ */

export const INFO: Record<string, { title: string; content: string }> = {
  impressions: {
    title: 'Impressions',
    content:
      'How many times your ads were shown across every connected platform, added together.',
  },
  spend: {
    title: 'Spend',
    content:
      'What the platforms charged for these ads, in your ad account currency. Accounts in different currencies are never added together.',
  },
  results: {
    title: 'Results',
    content:
      'Leads, purchases and sign-ups the platforms attributed to these ads, each counted once. Needs your pixel or conversion tag connected.',
  },
  cost_per_result: {
    title: 'Cost per result',
    content:
      'Spend divided by results. Lower is better. Compare it within one kind of result: a lead and a sale cost very different amounts.',
  },
  click_rate: {
    title: 'Click rate',
    content:
      'Share of people who saw the ad and clicked. Above 1% is healthy for most products.',
  },
  conversions: {
    title: 'Conversions',
    content:
      'Purchases or sign-ups the platforms attributed to these ads. Needs your pixel or tag connected.',
  },
  roas: {
    title: 'ROAS',
    content:
      'Tracked sales value divided by spend. 3.0× means every 1 spent brought back 3. Shown only when your platforms report sale values.',
  },
  credits: {
    title: 'Credits',
    content:
      'Each generation shows its credit cost before you start. A generation that fails costs nothing.',
  },
  typography_only: {
    title: 'Typography-only vs Generate with AI',
    content:
      'Typography-only lays your own product photo out with type — fast, and no image is invented. Generate with AI creates a new background or scene around it.',
  },
  brand_lock: {
    title: 'Brand colours and logo',
    content:
      'Locks the output to your brand palette and places your logo. Turn off for one-off creatives that sit outside the brand.',
  },
  product_images: {
    title: 'Product images',
    content:
      'Two to four photos from different angles give the AI more to work with than one. Plain backgrounds work best.',
  },
  calendar_legend: {
    title: 'A and P',
    content:
      'A is a paid ad campaign. P is an organic post. Colour shows status: scheduled, published or failed.',
  },
  org_separation: {
    title: 'Organizations',
    content:
      'Campaigns, creatives, media, budgets and connected accounts belong to one organization and never cross into another.',
  },
};

/* ------------------------------------------------------------------ */
/* Blocker banners                                                     */
/* ------------------------------------------------------------------ */

export const BLOCKER = {
  no_connection: {
    tone: 'error' as const,
    title: 'This organization has no ad account connected',
    body:
      'You can build and save campaigns now — publishing needs a connection.',
    cta: { label: 'Connect', route: '/settings?tab=connections' },
  },
  no_brand_kit: {
    tone: 'info' as const,
    title:
      'Add your logo and colours once — every creative after this comes out on-brand',
    body: 'Takes 40 seconds. We can pull the palette straight out of your logo.',
    cta: { label: 'Open your brand', route: '/business?topic=voice' },
    dismissible: true,
  },
  low_credits: {
    tone: 'warning' as const,
    title: 'Credits are running low',
    body: 'Top up to keep generating. Failed generations never cost credits.',
    cta: { label: 'Top up', route: '/credits' },
    dismissible: true,
  },
} as const;
